import React, { useEffect, useMemo, useState } from 'react';
import { Car, Download, Edit3, Plus, Save, Search, Trash2, X } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import VehicleTypeIcon, { getVehicleTypeLabel } from '../components/VehicleTypeIcon';
import { vehicleService } from '../services/api';
import { useVehicleStore } from '../store';
import { toast } from 'react-toastify';
import { exportCsv } from '../utils/csv';

const emptyForm = {
  name: '',
  plate: '',
  brand: '',
  model: '',
  year: '',
  color: '',
  type: 'car',
  imei: '',
  sim_card: '',
  tracker_model: '',
  notes: '',
};

const vehicleTypes = {
  car: 'Carro',
  motorcycle: 'Moto',
  truck: 'Caminhão',
  van: 'Van',
  bus: 'Ônibus',
  other: 'Outro',
};

const Vehicles = () => {
  const { vehicles, setVehicles, addVehicle, updateVehicle, removeVehicle, setLoading, loading } = useVehicleStore();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    setLoading(true);

    try {
      const response = await vehicleService.getAll();
      setVehicles(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar veículos');
    } finally {
      setLoading(false);
    }
  };

  const filteredVehicles = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return vehicles;

    return vehicles.filter((vehicle) => (
      vehicle.name?.toLowerCase().includes(term) ||
      vehicle.plate?.toLowerCase().includes(term) ||
      vehicle.brand?.toLowerCase().includes(term) ||
      vehicle.model?.toLowerCase().includes(term) ||
      vehicle.imei?.toLowerCase().includes(term)
    ));
  }, [search, vehicles]);

  const openCreateModal = () => {
    setEditingVehicle(null);
    setFormData(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name || '',
      plate: vehicle.plate || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      color: vehicle.color || '',
      type: vehicle.type || 'car',
      imei: vehicle.imei || '',
      sim_card: vehicle.sim_card || '',
      tracker_model: vehicle.tracker_model || '',
      notes: vehicle.notes || '',
    });
    setModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      if (editingVehicle) {
        const response = await vehicleService.update(editingVehicle.id, formData);
        updateVehicle(editingVehicle.id, response.data.data);
        toast.success('Veículo atualizado com sucesso');
      } else {
        const response = await vehicleService.create(formData);
        addVehicle(response.data.data);
        toast.success('Veículo cadastrado com sucesso');
      }

      setModalOpen(false);
      setFormData(emptyForm);
      setEditingVehicle(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar veículo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vehicle) => {
    const confirmed = window.confirm(`Remover ${vehicle.name}? Esta ação não pode ser desfeita.`);
    if (!confirmed) return;

    try {
      await vehicleService.delete(vehicle.id);
      removeVehicle(vehicle.id);
      toast.success('Veículo removido com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover veículo');
    }
  };

  const handleExport = () => {
    exportCsv('rcrastro-veiculos.csv', [
      { key: 'name', label: 'Nome' },
      { key: 'plate', label: 'Placa' },
      { key: 'brand', label: 'Marca' },
      { key: 'model', label: 'Modelo' },
      { key: 'year', label: 'Ano' },
      { key: 'type', label: 'Tipo' },
      { key: 'imei', label: 'IMEI' },
      { key: 'tracker_model', label: 'Rastreador' },
      { key: 'status', label: 'Status' },
    ], filteredVehicles.map((vehicle) => ({
      ...vehicle,
      type: vehicleTypes[vehicle.type] || vehicle.type,
      status: vehicle.is_blocked ? 'Bloqueado' : vehicle.is_active ? 'Ativo' : 'Offline',
    })));
    toast.success('CSV de veículos gerado');
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Veículos</h1>
              <p className="text-dark-400">Cadastre, edite e acompanhe os rastreadores da frota.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleExport}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </button>
              <button
                type="button"
                onClick={openCreateModal}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Novo veículo
              </button>
            </div>
          </div>

          <div className="card mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dark-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="input-field pl-11"
                placeholder="Buscar por nome, placa, modelo ou IMEI"
              />
            </div>
          </div>

          <div className="card p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left">
                <thead className="border-b border-dark-700 text-sm text-dark-400">
                  <tr>
                    <th className="px-5 py-4 font-medium">Veículo</th>
                    <th className="px-5 py-4 font-medium">Placa</th>
                    <th className="px-5 py-4 font-medium">Tipo</th>
                    <th className="px-5 py-4 font-medium">Rastreador</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 text-right font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        Carregando veículos...
                      </td>
                    </tr>
                  ) : filteredVehicles.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        <Car className="mx-auto mb-2 h-10 w-10 opacity-50" />
                        Nenhum veículo encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredVehicles.map((vehicle) => (
                      <tr key={vehicle.id} className="border-b border-dark-800 text-sm last:border-0">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-white">{vehicle.name}</div>
                          <div className="text-dark-400">
                            {[vehicle.brand, vehicle.model, vehicle.year].filter(Boolean).join(' ')}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-white">{vehicle.plate}</td>
                        <td className="px-5 py-4 text-dark-300">
                          <span className="flex items-center gap-2">
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-primary-500/25 bg-primary-500/10 text-primary-300">
                              <VehicleTypeIcon vehicle={vehicle} className="h-4 w-4" />
                            </span>
                            {getVehicleTypeLabel(vehicle)}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-dark-300">
                          <div>{vehicle.imei || 'Sem IMEI'}</div>
                          <div className="text-xs text-dark-500">{vehicle.tracker_model || 'Modelo não informado'}</div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`badge ${vehicle.is_blocked ? 'badge-danger' : vehicle.is_active ? 'badge-success' : 'badge-warning'}`}>
                            {vehicle.is_blocked ? 'Bloqueado' : vehicle.is_active ? 'Ativo' : 'Offline'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(vehicle)}
                              className="rounded-lg p-2 text-primary-400 transition-colors hover:bg-dark-700"
                              title="Editar"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(vehicle)}
                              className="rounded-lg p-2 text-danger-400 transition-colors hover:bg-dark-700"
                              title="Remover"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-dark-700 bg-dark-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-dark-800 p-5">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {editingVehicle ? 'Editar veículo' : 'Cadastrar veículo'}
                </h2>
                <p className="text-sm text-dark-400">Preencha os dados do veículo e rastreador.</p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-2 text-dark-400 transition-colors hover:bg-dark-800 hover:text-white"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Nome</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Ex: Frota Centro"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Placa</label>
                  <input
                    name="plate"
                    value={formData.plate}
                    onChange={handleChange}
                    className="input-field uppercase"
                    placeholder="ABC-1234"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Marca</label>
                  <input
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Fiat"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Modelo</label>
                  <input
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Strada"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Ano</label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    className="input-field"
                    min="1950"
                    max="2100"
                    placeholder="2024"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Cor</label>
                  <input
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Branco"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Tipo</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="input-field"
                  >
                    <option value="car">Carro</option>
                    <option value="motorcycle">Moto</option>
                    <option value="truck">Caminhão</option>
                    <option value="van">Van</option>
                    <option value="bus">Ônibus</option>
                    <option value="other">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">IMEI</label>
                  <input
                    name="imei"
                    value={formData.imei}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="860000000000000"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Chip</label>
                  <input
                    name="sim_card"
                    value={formData.sim_card}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Número do chip"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Modelo do rastreador</label>
                  <input
                    name="tracker_model"
                    value={formData.tracker_model}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="J16"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">Observações</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="input-field min-h-24"
                  placeholder="Informações internas do veículo"
                />
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-dark-800 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Salvando...' : 'Salvar veículo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
